import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { lazy, Suspense, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { IsSuperAdmin, useAuth } from "../context/auth.context";
import {
	reactPlugin,
	useTrackPageViews
} from "../services/app-insights.service";

import "./routes-container.scss";

import Loader from "../components/Loader";
import Nav from "../components/Nav/Nav";

const Admin = lazy(() => import("./Admin"));
const Changelogs = lazy(() => import("./Admin/Changelogs"));
const Feedback = lazy(() => import("./Admin/Feedback"));
const Observability = lazy(() => import("./Admin/Observability"));
const Organisations = lazy(() => import("./Admin/Organisations"));
const DiagnosticCache = lazy(
	() => import("./Admin/Organisations/DiagnosticCache")
);
const TabCommentaryCache = lazy(
	() => import("./Admin/Organisations/TabCommentaryCache")
);
const OrganisationConfiguration = lazy(
	() => import("./Admin/Organisations/Configuration")
);
const AtlasConversations = lazy(
	() => import("./Admin/Organisations/AtlasConversations")
);
const KeyTypeConfiguration = lazy(
	() => import("./Admin/Organisations/KeyTypeConfiguration")
);
const ReportTabs = lazy(() => import("./Admin/Organisations/ReportTabs"));
const OrganisationSSOConfiguration = lazy(
	() =>
		import("./Admin/Organisations/SSOConfiguration/OrganisationSSOConfiguration")
);
const TableSettings = lazy(() => import("./Admin/Organisations/TableSettings"));
const Structure = lazy(() => import("./Admin/Tables/Structure"));
const Testing = lazy(() => import("./Admin/Testing"));
const Users = lazy(() => import("./Admin/Users"));
const ForgotPassword = lazy(() => import("./ForgotPassword"));
const Home = lazy(() => import("./Home"));
const NotFound = lazy(() => import("./NotFound"));
const ModalPage = lazy(() => import("./Report/ModalPage"));
const Report = lazy(() => import("./Report/v1"));
const ReportV2 = lazy(() => import("./Report/v2"));
const ReportDemo = lazy(() => import("./ReportDemo"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const SigninPage = lazy(() => import("./SigninPage"));
const SSOCallback = lazy(() => import("./SSOCallback/SSOCallback"));
const TestSignalR = lazy(() => import("./TestSignalR"));
const TestWebhook = lazy(() => import("./TestWebhook"));
const ThankYou = lazy(() => import("./ThankYou"));

// ProtectedRoute component for v6
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
	const { isAuthenticated } = useAuth();
	const location = useLocation();

	if (!isAuthenticated) {
		return (
			<Navigate
				to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
				replace
			/>
		);
	}

	return children;
};

const SuperAdminProtectedRoute = ({ children }: { children: JSX.Element }) => {
	const { isAuthenticated } = useAuth();
	const location = useLocation();

	if (!IsSuperAdmin()) return <Navigate to={`/`} replace />;

	if (!isAuthenticated) {
		return (
			<Navigate
				to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
				replace
			/>
		);
	}

	return children;
};

const RouteFallback = () => (
	<div className="routes__suspense-loader">
		<Loader size={80} />
	</div>
);

const AppRoutes = () => {
	const { isAuthenticated } = useAuth();
	const location = useLocation();

	const [collapseNav, setCollapseNav] = useState(true);
	const [chatGPTModel, setChatGPTModel]: any = useState("gpt4o");

	useTrackPageViews();

	const publicRoutes = [
		"/login",
		"/reset-password",
		"/forgot-password",
		"/sso-callback"
	];
	const isPublicRoute = publicRoutes.includes(location.pathname);

	const handleNavChange = (change: any) => {
		setCollapseNav(!change);
	};

	return (
		<>
			{!isPublicRoute && (
				<Nav setCollapseNav={handleNavChange} collapseNav={collapseNav} />
			)}
			<div
				className={`routes ${isPublicRoute ? "public-route" : ""}
          ${!isPublicRoute && collapseNav ? "collapsed-nav" : ""}
          ${!isPublicRoute && !collapseNav ? "expanded-nav" : ""}`}
			>
				<Suspense fallback={<RouteFallback />}>
					<Routes>
						{/* Public Routes */}
						<Route path="/not-found" element={<NotFound />} />
						<Route path="/login" element={<SigninPage />} />
						<Route path="/reset-password" element={<ResetPassword />} />
						<Route path="/forgot-password" element={<ForgotPassword />} />
						<Route path="/sso-callback" element={<SSOCallback />} />

						{/* Private Routes */}
						<Route
							path="/"
							element={
								<ProtectedRoute>
									<Admin />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/report"
							element={
								<ProtectedRoute>
									<ReportDemo />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/reports/:reportId"
							element={
								<ProtectedRoute>
									<Report />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/reports/v2/:reportId"
							element={
								<ProtectedRoute>
									<ReportV2 />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/reports/modal/:reportId"
							element={
								<ProtectedRoute>
									<ModalPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/home"
							element={
								<ProtectedRoute>
									<Home />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin"
							element={
								<ProtectedRoute>
									<Admin />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations"
							element={
								<SuperAdminProtectedRoute>
									<Organisations />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId"
							element={
								<SuperAdminProtectedRoute>
									<OrganisationConfiguration />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/keyTypes"
							element={
								<ProtectedRoute>
									<KeyTypeConfiguration />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/diagnosticCache"
							element={
								<ProtectedRoute>
									<DiagnosticCache />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/tabCommentaryCache"
							element={
								<ProtectedRoute>
									<TabCommentaryCache />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/reportTabs"
							element={
								<SuperAdminProtectedRoute>
									<ReportTabs />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/sso"
							element={
								<SuperAdminProtectedRoute>
									<OrganisationSSOConfiguration />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/atlas-conversations"
							element={
								<SuperAdminProtectedRoute>
									<AtlasConversations />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/tables"
							element={
								<ProtectedRoute>
									<TableSettings />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/organisations/:organisationId/tables/:tableId"
							element={
								<ProtectedRoute>
									<Structure />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/changelogs"
							element={
								<SuperAdminProtectedRoute>
									<Changelogs />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/observability"
							element={
								<SuperAdminProtectedRoute>
									<Observability />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/testing"
							element={
								<SuperAdminProtectedRoute>
									<Testing />
								</SuperAdminProtectedRoute>
							}
						/>
						<Route
							path="/admin/users"
							element={
								<ProtectedRoute>
									<Users />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin/feedback"
							element={
								<ProtectedRoute>
									<Feedback />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/signalR"
							element={
								<ProtectedRoute>
									<TestSignalR />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/webhook"
							element={
								<ProtectedRoute>
									<TestWebhook />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/thank-you"
							element={
								<ProtectedRoute>
									<ThankYou />
								</ProtectedRoute>
							}
						/>
					</Routes>
				</Suspense>
			</div>
		</>
	);
};

export default withAITracking(reactPlugin, AppRoutes);
